import { Injectable } from '@angular/core';
import { ApiService } from '../../core/api.service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface Adesao {
  id: string;
  protocolo: string;
  protocoloAceite: string;
  nomeFantasia: string;
  instagramOficial: string;
  cnpjCpf: string;
  nomeResponsavel: string;
  whatsapp: string;
  email: string;
  autorizaFotos: boolean;
  aceitaTermos: boolean;
  dataAceite: string;
  dataAceiteServidorUtc: string;
  status: string;
  versaoContrato: string;
  hashContrato: string;
  dataCriacao: string;
  dataAtualizacao: string | null;
  ipOrigem: string;
  userAgent: string;
  fornecedorId: string | null;
}

export interface AdesaoDetalhe {
  id: string;
  status: string;
  fornecedorId: string | null;
  informacoesPessoais: any;
  statusEDatas: any;
  pagamento: any;
  auditoria: any;
  acoes: any;
}

@Injectable({ providedIn: 'root' })
export class AdesoesService {
  constructor(private api: ApiService) {}

  list(page = 1, pageSize = 10, filters?: any): Observable<{ data: Adesao[]; meta: any }> {
    return this.api.get<{ items: Adesao[]; totalItems: number; page: number; pageSize: number; totalPages: number }>('/admin/fornecedores/adesoes', { page, pageSize, ...(filters || {}) }).pipe(
      // Mapear a resposta do backend para o formato esperado
      map(response => ({
        data: response.items || [],
        meta: {
          total: response.totalItems,
          totalItems: response.totalItems,
          page: response.page,
          pageSize: response.pageSize,
          totalPages: response.totalPages
        }
      }))
    );
  }

  getById(id: string): Observable<AdesaoDetalhe> {
    return this.api.get<any>(`/admin/fornecedores/adesoes/${id}`).pipe(
      map(adesao => ({
        id: adesao.id,
        status: adesao.status,
        fornecedorId: adesao.fornecedorId || null,
        informacoesPessoais: {
          nomeFantasia: adesao.nomeFantasia,
          nomeResponsavel: adesao.nomeResponsavel,
          cnpjCpf: adesao.cnpjCpf,
          email: adesao.email,
          whatsapp: adesao.whatsapp,
          instagramOficial: adesao.instagramOficial,
          autorizaFotos: adesao.autorizaFotos
        },
        statusEDatas: {
          statusAtual: adesao.status,
          historico: [],
          dataCriacao: adesao.dataCriacao,
          dataAceite: adesao.dataAceite,
          dataAtualizacao: adesao.dataAtualizacao
        },
        pagamento: {
          status: adesao.status === 'ATIVO' ? 'Pago' : 'Aguardando',
          dataPagamento: adesao.dataAceite,
          valor: 'A definir'
        },
        auditoria: {
          protocolo: {
            numero: adesao.protocolo,
            dataGeracao: adesao.dataCriacao
          }
        },
        acoes: {
          canAtivar: adesao.status === 'AGUARDANDO_PAGAMENTO',
          canDesativar: adesao.status === 'ATIVO',
          canAprovar: adesao.status === 'PENDENTE_APROVACAO'
        }
      }))
    );
  }

  getAuditoria(id: string): Observable<any> {
    return this.api.get<any>(`/admin/fornecedores/adesoes/${id}/audit-trail`).pipe(
      map(adesao => ({
        protocolo: {
          numero: adesao.protocolo,
          dataGeracao: adesao.dataCriacao
        },
        fornecedor: {
          nome: adesao.nomeFantasia,
          cpfCnpj: adesao.cnpjCpf,
          email: adesao.email,
          responsavel: adesao.nomeResponsavel,
          whatsapp: adesao.whatsapp,
          instagram: adesao.instagramOficial
        },
        aceite: {
          dataAceite: adesao.dataAceite,
          timezone: 'UTC-3',
          aceptouTermosUso: adesao.aceitaTermos,
          autorizouUsoFotos: adesao.autorizaFotos
        },
        metadadosTecnicos: {
          ipOrigem: adesao.ipOrigem,
          ipForwardedFor: adesao.ipForwardedFor,
          userAgent: adesao.userAgent,
          dispositivo: {
            tipo: 'Desktop',
            sistemaOperacional: 'Windows',
            navegador: 'Chrome',
            versao: '143.0'
          }
        },
        termo: {
          versao: adesao.versaoContrato,
          dataVersao: adesao.dataCriacao,
          tamanho: '2.5 MB',
          hash: adesao.hashContrato
        },
        integridade: {
          hashCorreto: true,
          hashVerificado: adesao.hashContrato,
          dataVerificacao: new Date(),
          assinatura: 'Válida'
        },
        validacoes: [
          {
            validacao: 'Termos de Uso',
            resultado: adesao.aceitaTermos ? 'PASSOU' : 'FALHOU',
            detalhes: adesao.aceitaTermos ? 'Termos aceitos' : 'Termos não aceitos'
          },
          {
            validacao: 'Autorização de Fotos',
            resultado: adesao.autorizaFotos ? 'PASSOU' : 'FALHOU',
            detalhes: adesao.autorizaFotos ? 'Autorizado' : 'Não autorizado'
          },
          {
            validacao: 'IP de Origem',
            resultado: 'PASSOU',
            detalhes: `IP registrado: ${adesao.ipOrigem}`
          },
          {
            validacao: 'Hash do Contrato',
            resultado: 'PASSOU',
            detalhes: `Hash verificado: ${adesao.hashContrato.substring(0, 20)}...`
          }
        ]
      }))
    );
  }

  getTermo(id: string): Observable<any> {
    return this.api.get<any>(`/admin/fornecedores/adesoes/${id}/audit-trail/termo`).pipe(
      map(response => ({
        adesaoId: response.adesaoId,
        protocolo: response.protocolo,
        versaoTermo: response.versaoTermo,
        hashTermo: response.hashTermo,
        dataAceite: response.dataAceite,
        conteudo: response.termo
      }))
    );
  }

  approveFreePlan(fornecedorId: string): Observable<any> {
    return this.api.post(`/admin/fornecedores/${fornecedorId}/approve-free-plan`, {});
  }

  ativarFornecedor(id: string): Observable<any> {
    return this.api.post(`/admin/fornecedores/adesoes/${id}/confirmar-pagamento`, {});
  }

  desativarFornecedor(id: string): Observable<any> {
    return this.api.patch(`/admin/fornecedores/adesoes/${id}/desativar`, {});
  }

  reenviarCredenciais(id: string): Observable<any> {
    return this.api.post(`/admin/fornecedores/adesoes/${id}/reenviar-credenciais`, {});
  }

  exportarPdf(id: string): Observable<Blob> {
    return this.api.get(`/admin/fornecedores/adesoes/${id}/audit-trail/pdf`, { responseType: 'blob' });
  }
}
